import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import type { SanitizedUser } from '@movo/contracts/api/users';
import type { UserRole } from '@movo/contracts/domain/user';
import { useAppAbility, useSession } from '@/features/auth';
import type { ApiError } from '@/shared/lib/apiClient';
import { useSetShellHeader } from '@/shared/shell/ShellHeaderContext';
import {
  useChangeUserRole,
  useCreateUser,
  useDeleteUser,
  useSetUserActive,
  useSetUserPassword,
  useUsersQuery,
} from '../api/queries';
import { userPermissions } from '../model/permissions';
import {
  EMPTY_FILTERS,
  sameFilters,
  toListParams,
  totalPagesOf,
  usersSubtitle,
  type NewUserForm,
} from '../model/users';

type PasswordTarget = Pick<SanitizedUser, '_id' | 'email' | 'username'>;

/** Stato e azioni della pagina Utenti: filtri, paginazione, lista, scritture e dialoghi. */
export function useUsersScreen() {
  const { user: currentUser } = useSession();
  const ability = useAppAbility();
  const permissions = useMemo(() => userPermissions(ability, currentUser), [ability, currentUser]);

  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [creating, setCreating] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState<PasswordTarget | null>(null);
  const [deleting, setDeleting] = useState<SanitizedUser | null>(null);

  const list = useUsersQuery(toListParams(filters, page, limit));
  const createMutation = useCreateUser();
  const roleMutation = useChangeUserRole();
  const activeMutation = useSetUserActive();
  const passwordMutation = useSetUserPassword();
  const deleteMutation = useDeleteUser();

  const total = list.data?.total;
  const maxPage = totalPagesOf(total ?? 0, limit);

  useSetShellHeader({
    title: 'Utenti',
    subtitle: total === undefined ? undefined : usersSubtitle(total),
  });

  const runAction = async <V>(
    mutate: (variables: V) => Promise<unknown>,
    variables: V,
    success: string
  ) => {
    try {
      await mutate(variables);
      toast.success(success);
    } catch (error) {
      toast.error((error as ApiError).message);
    }
  };

  return {
    rows: (list.data?.items ?? []).map((user) => ({ user, ...permissions.forRow(user) })),
    isLoading: list.isPending,
    isRefreshing: list.isFetching && !list.isPending,
    listError: list.error?.message,
    retry: () => list.refetch(),

    filterRoles: permissions.readableRoles,
    draftFilters,
    setDraftFilters,
    applyFilters() {
      if (sameFilters(draftFilters, filters) && page === 1) {
        list.refetch();
        return;
      }
      setFilters(draftFilters);
      setPage(1);
    },

    pagination: {
      page,
      pageSize: limit,
      total: total ?? 0,
      maxPage,
      onPageChange: setPage,
      onPageSizeChange(size: number) {
        setLimit(size);
        setPage(1);
      },
    },

    canCreate: permissions.creatableRoles.length > 0,
    openCreate() {
      createMutation.reset();
      setCreating(true);
    },
    createDialog: creating
      ? {
          roleOptions: permissions.creatableRoles,
          submitting: createMutation.isPending,
          error: createMutation.error?.message,
          onClose: () => setCreating(false),
          onSubmit: (form: NewUserForm) =>
            createMutation.mutate(form, {
              onSuccess: () => {
                setCreating(false);
                setPage(1);
                toast.success('Utente creato');
              },
            }),
        }
      : null,

    changeRole: (id: string, role: UserRole) =>
      runAction(roleMutation.mutateAsync, { id, role }, 'Ruolo aggiornato'),
    toggleActive: (user: Pick<SanitizedUser, '_id' | 'status'>) =>
      runAction(
        activeMutation.mutateAsync,
        { id: user._id, isActive: !user.status.isActive },
        user.status.isActive ? 'Utente disattivato' : 'Utente attivato'
      ),

    askDelete: setDeleting,
    deleteDialog: deleting
      ? {
          email: deleting.email,
          isLoading: deleteMutation.isPending,
          onCancel: () => setDeleting(null),
          async onConfirm() {
            await runAction(deleteMutation.mutateAsync, deleting._id, 'Utente eliminato');
            setDeleting(null);
          },
        }
      : null,

    openPasswordDialog(user: PasswordTarget) {
      passwordMutation.reset();
      setPasswordTarget(user);
    },
    // Il rifiuto del server resta nel dialogo, che copre la pagina.
    passwordDialog: passwordTarget
      ? {
          target: { label: passwordTarget.username, email: passwordTarget.email },
          saving: passwordMutation.isPending,
          error: passwordMutation.error?.message,
          onClose: () => setPasswordTarget(null),
          onSubmit: (newPassword: string) =>
            passwordMutation.mutate(
              { id: passwordTarget._id, newPassword },
              {
                onSuccess: () => {
                  setPasswordTarget(null);
                  toast.success(`Password aggiornata per ${passwordTarget.email}`);
                },
              }
            ),
        }
      : null,
  };
}
